import type { ReactNode } from "react";
import { apiErrorText } from "../api.js";

/**
 * A READ THAT DID NOT COMPLETE — said so the reader knows it was not their doing.
 * ===========================================================================
 *
 * Every load failure in this app rendered as a single terse line: "Could not
 * load your requests." True, and a dead end. A person who has just handed over
 * their deed papers and is met with one red sentence has no way to tell whether
 * they broke something, whether their case is damaged, or whether it is worth
 * pressing anything — so the gap gets filled by the worst available assumption,
 * which on this product is "my land documents have gone".
 *
 * So this adds the two things the bare line left out, and nothing else.
 *
 * WHY "NOTHING WAS CHANGED" IS SAFE TO SAY HERE, and why this component may only
 * be used on reads. Every call site is a GET — a case, a document list, a
 * profile, an account. A GET that failed altered no record, so the sentence is a
 * statement about what the request WAS, not a promise about what the server did
 * with it. Put this on a mutation and it becomes a false comfort of exactly the
 * class this codebase has already shipped five times: a failed POST may well
 * have written before the response was lost. Mutations state their own outcome,
 * next to the thing that failed, and they do it in their own words.
 *
 * WHAT IT DOES NOT DO. It does not promise a reload will work — "usually" is
 * doing real work in that sentence and must stay. It does not guess at a cause;
 * the server's own message is passed through untouched by apiErrorText wherever
 * it wrote one, and where it wrote none this invents nothing.
 *
 * `role="alert"` is kept, so the failure is still announced. The stylesheet's
 * default alert is now the warm amber panel rather than the red one, which is
 * the correct weight for something a reload fixes.
 */
export function LoadFailure({
  error,
  fallback,
  format,
  children,
}: {
  error: unknown;
  /** What could not be loaded, in a sentence, for when the server explained nothing. */
  fallback: string;
  /**
   * How to turn the error into a sentence. Defaults to `apiErrorText`.
   *
   * It exists for CaseMeetings, whose `meetingErrorText` says things
   * `apiErrorText` cannot: a zod 400 arrives as the bare token "invalid_request"
   * with the per-field detail dropped, and a past proposed time arrives as an
   * unexplained 500 because the rule lives only in a database trigger. Both get
   * careful, deliberately non-committal wording there. Hard-coding `apiErrorText`
   * here would have quietly thrown that away for a warmer wrapper, which is a bad
   * trade — the wrapper adds reassurance, and it must not cost the reader the
   * most specific true thing available.
   */
  format?: (error: unknown, fallback: string) => string;
  /** Anything this particular screen can add about what is still true despite the failure. */
  children?: ReactNode;
}) {
  return (
    <div role="alert">
      <p>{(format ?? apiErrorText)(error, fallback)}</p>
      <p>
        <small>
          This was a request to read something rather than to change it, so nothing on your
          side has been altered. Reloading the page usually clears it; if it keeps happening,
          that is ours to fix rather than anything for you to put right.
        </small>
      </p>
      {children}
    </div>
  );
}
